import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { it, enUS, es } from "date-fns/locale";
import { CalendarIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { BrutalSelect, BrutalSelectContent, BrutalSelectItem, BrutalSelectTrigger, BrutalSelectValue } from "@/components/ui/BrutalSelect";
import { DemographicsMultiSelect } from "./DemographicsMultiSelect";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { DemographicsData } from "./DemographicsForm";

interface DemographicsFieldsProps {
  data: Partial<DemographicsData>;
  onChange: (data: Partial<DemographicsData>) => void;
  disabled?: boolean;
}

export const DemographicsFields = ({
  data,
  onChange,
  disabled = false,
}: DemographicsFieldsProps) => {
  const { t, i18n } = useTranslation();

  const getDateLocale = () => {
    switch (i18n.language?.substring(0, 2)) {
      case "it": return it;
      case "es": return es;
      default: return enUS;
    }
  };

  const genderOptions = [
    { value: "woman", label: t("onboarding.options.gender.woman") },
    { value: "man", label: t("onboarding.options.gender.man") },
    { value: "non-binary", label: t("onboarding.options.gender.nonBinary") },
    { value: "other", label: t("onboarding.options.gender.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const orientationOptions = [
    { value: "heterosexual", label: t("onboarding.options.orientation.heterosexual") },
    { value: "gay", label: t("onboarding.options.orientation.gay") },
    { value: "lesbian", label: t("onboarding.options.orientation.lesbian") },
    { value: "bisexual", label: t("onboarding.options.orientation.bisexual") },
    { value: "pansexual", label: t("onboarding.options.orientation.pansexual") },
    { value: "asexual", label: t("onboarding.options.orientation.asexual") },
    { value: "queer", label: t("onboarding.options.orientation.queer") },
    { value: "other", label: t("onboarding.options.orientation.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const neurodivergenceOptions = [
    { value: "adhd", label: t("onboarding.options.neurodivergences.adhd") },
    { value: "autism", label: t("onboarding.options.neurodivergences.autism") },
    { value: "dyslexia", label: t("onboarding.options.neurodivergences.dyslexia") },
    { value: "other", label: t("onboarding.options.neurodivergences.other") },
    { value: "none", label: t("onboarding.options.neurodivergences.none") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const ethnicityOptions = [
    { value: "caucasian-european", label: t("onboarding.options.ethnicity.caucasian") },
    { value: "african", label: t("onboarding.options.ethnicity.african") },
    { value: "asian", label: t("onboarding.options.ethnicity.asian") },
    { value: "latin-american", label: t("onboarding.options.ethnicity.latinAmerican") },
    { value: "middle-eastern", label: t("onboarding.options.ethnicity.middleEastern") },
    { value: "mixed", label: t("onboarding.options.ethnicity.mixed") },
    { value: "other", label: t("onboarding.options.ethnicity.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const workStatusOptions = [
    { value: "employee", label: t("onboarding.options.workStatus.employee") },
    { value: "freelance", label: t("onboarding.options.workStatus.freelance") },
    { value: "entrepreneur", label: t("onboarding.options.workStatus.entrepreneur") },
    { value: "student", label: t("onboarding.options.workStatus.student") },
    { value: "job-seeker", label: t("onboarding.options.workStatus.jobSeeker") },
    { value: "other", label: t("onboarding.options.workStatus.other") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const disabilityOptions = [
    { value: "motor", label: t("onboarding.options.disabilities.motor") },
    { value: "visual", label: t("onboarding.options.disabilities.visual") },
    { value: "hearing", label: t("onboarding.options.disabilities.hearing") },
    { value: "invisible-chronic", label: t("onboarding.options.disabilities.invisibleChronic") },
    { value: "none", label: t("onboarding.options.disabilities.none") },
    { value: "prefer-not-to-say", label: t("onboarding.options.preferNotToSay") },
  ];

  const currentYear = new Date().getFullYear();
  
  const dateOfBirth = data.date_of_birth ? new Date(data.date_of_birth) : undefined;

  const handleDateChange = (date: Date | undefined) => {
    onChange({ ...data, date_of_birth: date || null });
  };

  const handleFieldChange = (field: keyof DemographicsData, value: any) => {
    onChange({ ...data, [field]: value });
  };

  return (
    <div className={cn("space-y-4", disabled && "opacity-50 pointer-events-none")}>
      {/* Data di nascita - Full width */}
      <div className="space-y-2">
        <Label>{t("onboarding.fields.dateOfBirth")}</Label>
        <Popover>
          <PopoverTrigger asChild>
            <div
              className={cn(
                "w-full border-[3px] border-dark bg-card px-3 py-3 font-body text-base",
                "flex items-center gap-2 cursor-pointer transition-all",
                "hover:shadow-[4px_4px_0px_0px] hover:shadow-dark hover:translate-x-[-2px] hover:translate-y-[-2px]",
                !dateOfBirth && "text-muted-foreground"
              )}
            >
              <CalendarIcon className="h-5 w-5 shrink-0" />
              <span className="flex-1">
                {dateOfBirth
                  ? format(dateOfBirth, "dd/MM/yyyy", { locale: getDateLocale() })
                  : t("onboarding.fields.selectDate")}
              </span>
            </div>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="start">
            <Calendar
              mode="single"
              selected={dateOfBirth}
              onSelect={handleDateChange}
              disabled={(date) => date > new Date() || date < new Date("1920-01-01")}
              captionLayout="dropdown"
              fromYear={1920}
              toYear={currentYear}
              initialFocus
              locale={getDateLocale()}
              className="p-3 pointer-events-auto"
            />
          </PopoverContent>
        </Popover>
        <p className="text-xs text-muted-foreground">
          {t("onboarding.fields.dateOfBirthHint")}
        </p>
      </div>

      {/* Genere | Orientamento - 2 colonne */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t("onboarding.fields.genderIdentity")}</Label>
          <BrutalSelect 
            value={data.gender_identity || ""} 
            onValueChange={(value) => handleFieldChange('gender_identity', value)}
          >
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t("onboarding.fields.selectOption")} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              {genderOptions.map((option) => (
                <BrutalSelectItem key={option.value} value={option.value}>
                  {option.label}
                </BrutalSelectItem>
              ))}
            </BrutalSelectContent>
          </BrutalSelect>
        </div>

        <div className="space-y-2">
          <Label>{t("onboarding.fields.sexualOrientation")}</Label>
          <BrutalSelect 
            value={data.sexual_orientation || ""} 
            onValueChange={(value) => handleFieldChange('sexual_orientation', value)}
          >
            <BrutalSelectTrigger>
              <BrutalSelectValue placeholder={t("onboarding.fields.selectOption")} />
            </BrutalSelectTrigger>
            <BrutalSelectContent>
              {orientationOptions.map((option) => (
                <BrutalSelectItem key={option.value} value={option.value}>
                  {option.label}
                </BrutalSelectItem>
              ))}
            </BrutalSelectContent>
          </BrutalSelect>
        </div>
      </div>

      {/* Etnia - Full width */}
      <div className="space-y-2">
        <Label>{t("onboarding.fields.ethnicity")}</Label>
        <BrutalSelect 
          value={data.ethnicity || ""} 
          onValueChange={(value) => handleFieldChange('ethnicity', value)}
        >
          <BrutalSelectTrigger>
            <BrutalSelectValue placeholder={t("onboarding.fields.selectOption")} />
          </BrutalSelectTrigger>
          <BrutalSelectContent>
            {ethnicityOptions.map((option) => (
              <BrutalSelectItem key={option.value} value={option.value}>
                {option.label}
              </BrutalSelectItem>
            ))}
          </BrutalSelectContent>
        </BrutalSelect>
      </div>

      {/* Neurodivergenza | Disabilità - 2 colonne */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>{t("onboarding.fields.neurodivergences")}</Label>
          <DemographicsMultiSelect
            options={neurodivergenceOptions}
            selected={data.neurodivergences || []}
            onChange={(selected) => handleFieldChange('neurodivergences', selected)}
            placeholder={t("onboarding.fields.selectMultiple")}
          />
        </div>

        <div className="space-y-2">
          <Label>{t("onboarding.fields.disabilities")}</Label>
          <DemographicsMultiSelect
            options={disabilityOptions}
            selected={data.disabilities || []}
            onChange={(selected) => handleFieldChange('disabilities', selected)}
            placeholder={t("onboarding.fields.selectMultiple")}
          />
        </div>
      </div>

      {/* Situazione lavorativa - Full width */}
      <div className="space-y-2">
        <Label>{t("onboarding.fields.workStatus")}</Label>
        <BrutalSelect 
          value={data.work_status || ""} 
          onValueChange={(value) => handleFieldChange('work_status', value)}
        >
          <BrutalSelectTrigger>
            <BrutalSelectValue placeholder={t("onboarding.fields.selectOption")} />
          </BrutalSelectTrigger>
          <BrutalSelectContent>
            {workStatusOptions.map((option) => (
              <BrutalSelectItem key={option.value} value={option.value}>
                {option.label}
              </BrutalSelectItem>
            ))}
          </BrutalSelectContent>
        </BrutalSelect>
      </div>
    </div>
  );
};
